import { browserTimeZone } from "./time-zones";

/**
 * Times, as each person reads them.
 *
 * The api stores and sends moments. A person reads a moment in their own time
 * zone — the one in their profile, or this device's when they have not chosen
 * one — and types times in that same zone, which this turns into a moment
 * before anything is sent. A coach in Lisbon typing 06:00 and a member in New
 * York reading 01:00 are looking at the same moment, and both are right.
 *
 * Everything here takes the zone as an argument rather than reading the
 * device's, so a profile's zone and a test's are honoured the same way.
 */

/** The zone this person reads times in, and whether they chose it. */
export function viewerZone(profileZone: string | null): { zone: string; chosen: boolean } {
  return profileZone ? { zone: profileZone, chosen: true } : { zone: browserTimeZone(), chosen: false };
}

type WallTime = { year: number; month: number; day: number; hour: number; minute: number };

function wallTimeAt(instant: number, zone: string): WallTime & { second: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(instant));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

/** How far `zone` is ahead of UTC at a moment, in milliseconds. */
function offsetAt(instant: number, zone: string): number {
  const wall = wallTimeAt(instant, zone);
  const asIfUTC = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
  // The wall time has whole seconds; compare it with the moment's.
  return asIfUTC - Math.floor(instant / 1000) * 1000;
}

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * A moment as a datetime-local input shows it to someone in `zone`:
 * "2026-09-27T06:00". What an edit form pre-fills.
 */
export function toWallTime(iso: string, zone: string): string {
  const wall = wallTimeAt(new Date(iso).getTime(), zone);
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`;
}

/**
 * What someone in `zone` typed into a datetime-local input, as a moment in ISO
 * 8601. Null when it is not a time.
 *
 * The offset at a first guess is right except within an hour of a clock
 * change; asking again at the corrected moment settles those. A time that
 * does not exist — 01:30 on the night the clocks jump from 01:00 to 02:00 —
 * lands an hour to one side, which is what a person typing it meant.
 */
export function fromWallTime(value: string, zone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) return null;
  const [year, month, day, hour, minute, second] = match.slice(1).map((part) => Number(part ?? 0));
  const guess = Date.UTC(year!, month! - 1, day!, hour!, minute!, second ?? 0);
  if (Number.isNaN(guess)) return null;
  let instant = guess - offsetAt(guess, zone);
  instant = guess - offsetAt(instant, zone);
  return new Date(instant).toISOString();
}

/**
 * A moment as someone in `zone` reads it: "Sat 27 Sep · 06:00", with the year
 * when it is not this one.
 *
 * Put together from parts rather than left to a locale, so it reads the same
 * in every browser — ICU's en-GB alone writes "Sept" in some versions and adds
 * commas in others — and every test reads the same string.
 */
export function formatMoment(iso: string, zone: string, now = new Date()): string {
  const wall = wallTimeAt(new Date(iso).getTime(), zone);
  return `${formatDay(iso, zone, now)} · ${pad(wall.hour)}:${pad(wall.minute)}`;
}

/** The month of a moment as someone in `zone` reads it: "March 2026". */
export function formatMonth(iso: string, zone: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, month: "long", year: "numeric" }).format(new Date(iso));
}

/** A moment's weekday and day of the month in `zone`, for a date block: { weekday: "Sat", day: 27 }. */
export function dayParts(iso: string, zone: string): { weekday: string; day: number } {
  const instant = new Date(iso).getTime();
  const weekday =
    new Intl.DateTimeFormat("en-US", { timeZone: zone, weekday: "short" })
      .formatToParts(new Date(instant))
      .find((part) => part.type === "weekday")?.value ?? "";
  return { weekday, day: wallTimeAt(instant, zone).day };
}

/** The day of a moment as someone in `zone` reads it: "Sat 27 Sep", as above. */
export function formatDay(iso: string, zone: string, now = new Date()): string {
  const instant = new Date(iso).getTime();
  const wall = wallTimeAt(instant, zone);
  const names = new Intl.DateTimeFormat("en-US", { timeZone: zone, weekday: "short", month: "short" })
    .formatToParts(new Date(instant));
  const name = (type: Intl.DateTimeFormatPartTypes) =>
    names.find((part) => part.type === type)?.value ?? "";
  const year = wall.year === wallTimeAt(now.getTime(), zone).year ? "" : ` ${wall.year}`;
  return `${name("weekday")} ${wall.day} ${name("month")}${year}`;
}

/**
 * A length of time typed in two boxes, minutes and seconds — a part's time
 * limit — in milliseconds. Minutes may go past 59 rather than needing a box
 * for hours; seconds are below 60. Both whole: a cap is not set to the tenth.
 * Nobody types milliseconds, or a colon.
 */
export function durationFromBoxes(
  minutes: string,
  seconds: string,
): { value: number } | { empty: true } | { error: string } {
  const min = minutes.trim();
  const sec = seconds.trim();
  if (min === "" && sec === "") return { empty: true };
  if (!/^\d*$/.test(min) || !/^\d*$/.test(sec)) return { error: "minutes and seconds are whole numbers" };
  const secondsPart = Number(sec || "0");
  if (secondsPart >= 60) return { error: "the seconds go up to 59 — put whole minutes in the minutes" };
  const total = Number(min || "0") * 60 + secondsPart;
  if (total <= 0) return { error: "a time limit is more than 0:00" };
  return { value: total * 1_000 };
}

/** What a length of time is counted in: an event ends "3 days" after it starts. */
export const LENGTH_UNITS = ["minutes", "hours", "days"] as const;
export type LengthUnit = (typeof LENGTH_UNITS)[number];

/** A unit as a person reads it after `amount`: "1 day", "3 days". */
export function unitLabel(unit: LengthUnit, amount: number): string {
  return amount === 1 ? unit.slice(0, -1) : unit;
}

/** A wall time as a date box's and a time box's values: "2026-09-27" and "06:00". "" for none. */
export function splitWall(wall: string): { date: string; time: string } {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(wall);
  return match ? { date: match[1]!, time: match[2]! } : { date: "", time: "" };
}

/** A date box's and a time box's values as one wall time, or null when either is not one. */
export function joinWall(date: string, time: string): string | null {
  const day = date.trim();
  const clock = time.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !/^\d{2}:\d{2}(?::\d{2})?$/.test(clock)) return null;
  return `${day}T${clock.slice(0, 5)}`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * The wall time a length after `start`, a wall time in `zone`, or null when
 * `start` is not one. Days are counted on the calendar, so 18:00 three days on
 * is 18:00 again across a clock change; minutes and hours on the clock, so two
 * hours are two hours even through one.
 */
export function wallAfter(start: string, amount: number, unit: LengthUnit, zone: string): string | null {
  const { date, time } = splitWall(start);
  if (!date) return null;
  if (unit === "days") {
    const [year, month, day] = date.split("-").map(Number);
    const moved = new Date(Date.UTC(year!, month! - 1, day! + amount));
    return `${moved.getUTCFullYear()}-${pad(moved.getUTCMonth() + 1)}-${pad(moved.getUTCDate())}T${time}`;
  }
  const from = fromWallTime(start, zone);
  if (!from) return null;
  return toWallTime(new Date(Date.parse(from) + amount * (unit === "hours" ? HOUR : MINUTE)).toISOString(), zone);
}

/**
 * How long after the moment `startsAt` the moment `endsAt` is, in the largest
 * unit that counts it whole — what an edit form opens a length on — or null
 * when none does: ninety seconds, or an end at or before the start. Days are
 * whole when the end's wall time in `zone` is the start's, on a later day.
 */
export function lengthBetween(
  startsAt: string,
  endsAt: string,
  zone: string,
): { amount: number; unit: LengthUnit } | null {
  const gap = Date.parse(endsAt) - Date.parse(startsAt);
  if (!(gap > 0) || gap % MINUTE !== 0) return null;
  const from = splitWall(toWallTime(startsAt, zone));
  const to = splitWall(toWallTime(endsAt, zone));
  if (from.time === to.time) {
    const days = Math.round((Date.parse(`${to.date}T00:00Z`) - Date.parse(`${from.date}T00:00Z`)) / DAY);
    if (days >= 1) return { amount: days, unit: "days" };
  }
  if (gap % HOUR === 0) return { amount: gap / HOUR, unit: "hours" };
  return { amount: gap / MINUTE, unit: "minutes" };
}
